/**
 * Hosting service — dashboard data aggregation for hosts.
 *
 * Singleton pattern. Throws ServiceError on failure.
 * All queries are scoped by host_id; RLS enforces access at DB level.
 */
import { supabase } from '../supabase'
import type { Cols, EmbedOne, WithEmbed } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import { formatMarketTransmissionText } from '../utils/transmission'
import type {
  HostStats,
  RecentBooking,
  EarningsSummary,
  MonthlyEarning,
  BlockedDateRange,
  SetBlockedDatesInput,
} from '../types/hosting.types'

// ── Constants ────────────────────────────────────────────────────────────────

const PENDING_STATUSES = [
  'pending_payment',
  'awaiting_host',
  'confirmed',
  'pending_pickup',
] as const
const ACTIVE_STATUSES = ['active', 'pending_return'] as const

// ── Row interfaces (match Supabase response shapes) ──────────────────────────

type RentalStatsRow = Cols<'rentals', 'host_payout_cents' | 'status'> & {
  payments?: { status: string }[] | null
}

/** Shape returned by the recent-bookings select in `getRecentBookings`. */
type RecentBookingRow = WithEmbed<
  Cols<
    'rentals',
    | 'id'
    | 'booking_code'
    | 'vehicle_id'
    | 'guest_id'
    | 'status'
    | 'start_at'
    | 'end_at'
    | 'total_cents'
    | 'host_payout_cents'
    | 'created_at'
  >,
  {
    vehicles: EmbedOne<'vehicles', 'title'>
    profiles: EmbedOne<'profiles', 'display_name' | 'avatar_url'>
  }
>

/**
 * Blocked ranges arrive from two places with the same shape: a `select('*')`
 * on `vehicle_availability` and the `replace_vehicle_blocked_dates` RPC, which
 * returns rows of that same table. Typing the mapper on the table row keeps
 * both callers honest.
 */
type BlockedDateRow = Cols<
  'vehicle_availability',
  'id' | 'vehicle_id' | 'start_date' | 'end_date' | 'is_available' | 'reason'
>

// ── Row mappers ──────────────────────────────────────────────────────────────

function mapRecentBooking(row: RecentBookingRow): RecentBooking {
  return {
    id: row.id,
    bookingCode: row.booking_code,
    vehicleId: row.vehicle_id,
    vehicleTitle: formatMarketTransmissionText(row.vehicles?.title),
    guestId: row.guest_id,
    guestDisplayName: row.profiles?.display_name ?? null,
    guestAvatarUrl: row.profiles?.avatar_url ?? null,
    status: row.status,
    startAt: row.start_at,
    endAt: row.end_at,
    totalCents: row.total_cents,
    hostPayoutCents: row.host_payout_cents,
    createdAt: row.created_at,
  }
}

function mapBlockedDate(row: BlockedDateRow): BlockedDateRange {
  return {
    id: row.id,
    vehicleId: row.vehicle_id,
    startDate: row.start_date,
    endDate: row.end_date,
    isAvailable: row.is_available,
    reason: row.reason,
  }
}

// ── Service ──────────────────────────────────────────────────────────────────

class HostingServiceImpl {
  /**
   * Aggregate host stats from rentals, vehicles, profile, and payouts.
   * When organizationId is present, scope fleet metrics to the agency instead
   * of the individual profile so managers see the full company fleet.
   * Uses 4 parallel queries — data volume per host is small.
   */
  async getHostStats(hostId: string, organizationId?: string | null): Promise<HostStats> {
    // `payments(status)` comes along because earnings must be derived from money
    // that actually moved, not from a status column. A rental reaching
    // 'completed' with zero captured payments used to count as revenue, and
    // production has two right now — E2E seeds, worth a phantom $59.50 and two
    // phantom trips on a real host's dashboard. Statuses are set by many code
    // paths; a captured payment is set by exactly one.
    const rentalsQuery = supabase
      .from('rentals')
      .select('host_payout_cents, status, payments(status)')
    const vehicleCountQuery = supabase
      .from('vehicles')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active')
      .is('deleted_at', null)

    if (organizationId) {
      rentalsQuery.eq('organization_id', organizationId)
      vehicleCountQuery.eq('organization_id', organizationId)
    } else {
      rentalsQuery.eq('host_id', hostId)
      vehicleCountQuery.eq('host_id', hostId)
    }

    const [rentalsResult, vehicleCountResult, profileResult, payoutsResult] = await Promise.all([
      // 1) All rentals for this host (lightweight: only 2 columns)
      rentalsQuery,

      // 2) Active vehicle count (head-only, no data transfer)
      vehicleCountQuery,

      // 3) Host rating from profile
      supabase
        .from('profiles')
        .select('host_rating_avg, host_rating_count')
        .eq('id', hostId)
        .single(),

      // 4) Pending payouts
      supabase
        .from('payouts')
        .select('amount_cents')
        .eq('profile_id', hostId)
        .in('status', ['pending', 'processing']),
    ])

    if (rentalsResult.error) throw ServiceError.fromSupabaseError(rentalsResult.error)
    if (vehicleCountResult.error) throw ServiceError.fromSupabaseError(vehicleCountResult.error)
    if (profileResult.error) throw ServiceError.fromSupabaseError(profileResult.error)
    if (payoutsResult.error) throw ServiceError.fromSupabaseError(payoutsResult.error)

    const rentals: RentalStatsRow[] = rentalsResult.data ?? []

    let totalEarningsCents = 0
    let pendingBookings = 0
    let activeTrips = 0
    let totalTrips = 0

    for (const r of rentals) {
      if (r.status === 'completed') {
        // A completed trip that was never paid for is not earnings. It is
        // either test residue or a defect upstream, and in both cases showing
        // it as money the host made is a lie the host will act on.
        const wasPaid = (r.payments ?? []).some((p) => p.status === 'captured')
        if (wasPaid) {
          totalEarningsCents += r.host_payout_cents
          totalTrips++
        }
      } else if ((PENDING_STATUSES as readonly string[]).includes(r.status)) {
        pendingBookings++
      } else if ((ACTIVE_STATUSES as readonly string[]).includes(r.status)) {
        activeTrips++
      }
    }

    const pendingPayoutCents = (payoutsResult.data ?? []).reduce(
      (sum, p) => sum + (p.amount_cents ?? 0),
      0,
    )

    return {
      totalEarningsCents,
      pendingPayoutCents,
      activeListings: vehicleCountResult.count ?? 0,
      pendingBookings,
      activeTrips,
      avgRating: profileResult.data?.host_rating_avg ?? null,
      ratingCount: profileResult.data?.host_rating_count ?? 0,
      totalTrips,
    }
  }

  /**
   * Recent bookings for the host with vehicle title and guest info.
   * Uses FK hint joins for disambiguation (rentals has 2 FK refs to profiles).
   */
  async getRecentBookings(
    hostId: string,
    limit = 10,
    organizationId?: string | null,
  ): Promise<RecentBooking[]> {
    let query = supabase
      .from('rentals')
      .select(
        `
        id, booking_code, vehicle_id, guest_id, status,
        start_at, end_at, total_cents, host_payout_cents, created_at,
        vehicles!rentals_vehicle_id_fkey ( title ),
        profiles!rentals_guest_id_fkey ( display_name, avatar_url )
      `,
      )
      .order('created_at', { ascending: false })
      .limit(limit)

    query = organizationId
      ? query.eq('organization_id', organizationId)
      : query.eq('host_id', hostId)

    const { data, error } = await query

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapRecentBooking)
  }

  /**
   * Monthly earnings breakdown for the last N months.
   * Groups completed rentals client-side and fills zero-value months.
   */
  async getEarningsSummary(hostId: string, months = 6): Promise<EarningsSummary> {
    const now = new Date()
    const startDate = new Date(now.getFullYear(), now.getMonth() - months, 1)
    const startIso = startDate.toISOString()

    // `payments!inner(status)` with the captured filter is the join doing the
    // work: the monthly earnings chart must show money that moved, for the same
    // reason getHostStats does. An inner join drops a completed rental with no
    // captured payment instead of charting it as a month's income.
    const { data, error } = await supabase
      .from('rentals')
      .select('host_payout_cents, end_at, duration_hours, payments!inner(status)')
      .eq('host_id', hostId)
      .eq('status', 'completed')
      .eq('payments.status', 'captured')
      .gte('end_at', startIso)
      .order('end_at', { ascending: true })

    if (error) throw ServiceError.fromSupabaseError(error)

    // Group by YYYY-MM
    const monthMap = new Map<
      string,
      { totalCents: number; rentalCount: number; rentedDays: number }
    >()

    for (const row of data ?? []) {
      const month = row.end_at.slice(0, 7) // "YYYY-MM"
      const existing = monthMap.get(month) ?? { totalCents: 0, rentalCount: 0, rentedDays: 0 }
      existing.totalCents += row.host_payout_cents
      existing.rentalCount++
      // Un día parcial cuenta como día: es lo mismo que hace `vehicles.rented_days`
      // en la base, y las dos superficies tienen que decir el mismo número.
      existing.rentedDays += Math.max(Math.ceil((row.duration_hours ?? 0) / 24), 1)
      monthMap.set(month, existing)
    }

    // Fill gaps with zero-value months for continuous chart axis
    const result: MonthlyEarning[] = []
    const cursor = new Date(startDate)

    while (cursor <= now) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`
      const entry = monthMap.get(key)
      result.push({
        month: key,
        totalCents: entry?.totalCents ?? 0,
        rentalCount: entry?.rentalCount ?? 0,
        rentedDays: entry?.rentedDays ?? 0,
      })
      cursor.setMonth(cursor.getMonth() + 1)
    }

    let grandTotalCents = 0
    let grandTotalRentals = 0
    let grandTotalRentedDays = 0
    for (const m of result) {
      grandTotalCents += m.totalCents
      grandTotalRentals += m.rentalCount
      grandTotalRentedDays += m.rentedDays
    }

    return { months: result, grandTotalCents, grandTotalRentals, grandTotalRentedDays }
  }

  /**
   * Get blocked/unavailable date ranges for a vehicle within a window.
   * Uses overlap query: range starts before window ends AND range ends after window starts.
   */
  async getBlockedDates(
    vehicleId: string,
    startDate: string,
    endDate: string,
  ): Promise<BlockedDateRange[]> {
    const { data, error } = await supabase
      .from('vehicle_availability')
      .select('*')
      .eq('vehicle_id', vehicleId)
      .lte('start_date', endDate)
      .gte('end_date', startDate)
      .order('start_date', { ascending: true })

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapBlockedDate)
  }

  /**
   * Atomically replace manual blocked dates within a window.
   * The database RPC preserves outside segments and serializes writes per vehicle.
   */
  async setBlockedDates(
    vehicleId: string,
    input: SetBlockedDatesInput,
  ): Promise<BlockedDateRange[]> {
    const { data, error } = await supabase.rpc('replace_vehicle_blocked_dates', {
      p_vehicle_id: vehicleId,
      p_window_start: input.windowStart,
      p_window_end: input.windowEnd,
      p_blocked_ranges: input.blockedRanges.map((r) => ({
        startDate: r.startDate,
        endDate: r.endDate,
        reason: r.reason ?? 'manual',
      })),
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapBlockedDate)
  }
}

export const hostingService = new HostingServiceImpl()
